import {
  useMutation,
  useQueryClient,
  UseMutationResult,
  UseMutationOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import {
  RecordPaymentDto,
  InvoiceWithRelationsDto,
  ResponseDto,
} from "@mimber/schema"
import { invoicesKeys } from "../invoices.utils"

interface RecordInvoicePaymentOptions extends Omit<
  UseMutationOptions<
    ResponseDto<InvoiceWithRelationsDto>,
    Error,
    RecordPaymentDto
  >,
  "mutationFn"
> {}

export const useRecordInvoicePayment = (
  uuid: string,
  options: RecordInvoicePaymentOptions = {}
): UseMutationResult<
  ResponseDto<InvoiceWithRelationsDto>,
  Error,
  RecordPaymentDto
> => {
  guardAppUsage("mimber-organization", "useRecordInvoicePayment")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async (data: RecordPaymentDto) => {
      const response = await server.post<ResponseDto<InvoiceWithRelationsDto>>(
        `invoices/${uuid}/payments`,
        data
      )
      return response.data
    },
    onSuccess: (response, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: invoicesKeys.findOne(uuid) })
      queryClient.invalidateQueries({ queryKey: invoicesKeys.findAll() })
      options?.onSuccess?.(response, variables, onMutateResult, context)
    },
  })
}
